import { motion, useScroll, useTransform } from 'framer-motion';
import { BookOpen, MessageCircle, Sparkles, Star, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const ReadingShowcaseView = () => {
  const [livro, setLivro] = useState(null);
  const [stats, setStats] = useState({
    leitoras: 0,
    comentarios: 0,
    avaliacao: 0,
  });
  const containerRef = useRef(null);

  useEffect(() => {
    fetch(`${import.meta.env.VITE_API_URL}/livros/destaque`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => setLivro(data))
      .catch(() => {
        fetch(`${import.meta.env.VITE_API_URL}/livros`)
          .then((r) => (r.ok ? r.json() : Promise.reject()))
          .then((data) => {
            if (data.length > 0) setLivro(data[data.length - 1]);
          })
          .catch(() => {});
      });

    // Mock de estatísticas (você pode conectar a dados reais)
    setStats({
      leitoras: Math.floor(Math.random() * 150) + 50,
      comentarios: Math.floor(Math.random() * 80) + 20,
      avaliacao: (Math.random() * 0.5 + 4.5).toFixed(1),
    });
  }, []);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  const yImage = useTransform(scrollYProgress, [0, 1], [40, -40]);

  if (!livro) return null;

  const mesLabel = livro.mesLabel ?? '';
  const mesCurto = mesLabel.split(' ')[0]?.toUpperCase() ?? '';

  return (
    <section
      ref={containerRef}
      className="relative w-full py-16 md:py-24 overflow-hidden"
    >
      {/* Premium Background */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-rose-50/30 to-white" />
        <motion.div
          animate={{ x: [0, 30, 0], y: [0, 50, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -top-32 -left-32 w-96 h-96 bg-rose-200/30 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ x: [0, -40, 0], y: [0, -30, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
          className="absolute top-1/3 right-[-5%] w-96 h-96 bg-rose-100/20 rounded-full blur-3xl"
        />
      </div>

      <div className="container mx-auto px-6 md:px-8 relative z-10 max-w-7xl">
        {/* Header com Título e Badge */}
        <div className="mb-12 lg:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex items-center gap-3 mb-6"
          >
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="w-10 h-10 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full flex items-center justify-center"
            >
              <BookOpen size={20} className="text-white" />
            </motion.div>
            <div>
              <h2 className="text-2xl md:text-4xl font-black text-slate-900 tracking-tight">
                {`Leitura de ${mesCurto}`}
              </h2>
              <p className="text-xs md:text-sm text-slate-500 uppercase tracking-widest font-medium mt-1">
                O que estamos lendo juntas
              </p>
            </div>
          </motion.div>
        </div>

        {/* Grid Principal 2 Colunas */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Coluna 1: Livro em Destaque (Aumentado) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="lg:col-span-5 flex justify-center lg:justify-start"
          >
            <motion.div
              style={{ y: yImage }}
              className="relative w-full max-w-sm"
            >
              {/* Sombra e Livro */}
              <div className="relative">
                {/* Glow Background */}
                <motion.div
                  animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.6, 0.4] }}
                  transition={{ duration: 5, repeat: Infinity }}
                  className="absolute -inset-8 bg-gradient-to-r from-rose-300/40 via-rose-200/20 to-rose-300/40 rounded-3xl blur-2xl -z-10"
                />

                {/* Card do Livro */}
                <motion.div
                  whileHover={{ y: -10 }}
                  transition={{ duration: 0.4 }}
                  className="relative group"
                >
                  <div className="absolute -inset-1 bg-gradient-to-r from-rose-200/60 via-rose-100/40 to-rose-200/60 rounded-3xl opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />

                  <div className="relative bg-white rounded-3xl overflow-hidden shadow-2xl border border-rose-100/50 backdrop-blur-sm">
                    {/* Imagem */}
                    <div className="aspect-[3/4] overflow-hidden bg-gradient-to-br from-rose-50 to-rose-100">
                      {livro.coverBase64 ? (
                        <motion.img
                          src={livro.coverBase64}
                          className="w-full h-full object-cover"
                          alt={livro.title}
                          whileHover={{ scale: 1.08 }}
                          transition={{ duration: 0.6 }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen size={60} className="text-rose-300" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
                    </div>

                    {/* Overlay de Informações */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                      <p className="text-white text-sm font-medium">
                        {livro.title.substring(0, 40)}
                      </p>
                    </div>
                  </div>

                  {/* Badge do Mês Flutuante */}
                  <motion.div
                    animate={{ y: [0, -8, 0], rotate: [0, 2, 0] }}
                    transition={{ duration: 4, repeat: Infinity }}
                    className="absolute -bottom-6 -right-6 bg-gradient-to-br from-white to-rose-50 rounded-2xl p-4 shadow-xl border border-rose-100/80 backdrop-blur-sm"
                  >
                    <div className="text-center">
                      <p className="text-xs font-black text-slate-500 uppercase tracking-widest">
                        Mês de
                      </p>
                      <p className="text-4xl font-black text-slate-900 leading-none">
                        {mesCurto}
                      </p>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 12,
                          repeat: Infinity,
                          ease: 'linear',
                        }}
                        className="absolute -top-2 -right-2 w-6 h-6"
                      >
                        <Sparkles size={24} className="text-rose-500" />
                      </motion.div>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>

          {/* Coluna 2: Conteúdo e CTAs */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="lg:col-span-7 flex flex-col justify-start"
          >
            {/* Título e Autor */}
            <div className="mb-6">
              <motion.h3
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 leading-tight tracking-tight mb-2"
              >
                {livro.title}
              </motion.h3>
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.35 }}
                className="text-lg md:text-xl text-slate-500 font-serif italic"
              >
                por{' '}
                <span className="font-semibold text-slate-700">
                  {livro.author}
                </span>
              </motion.p>
            </div>

            {/* Sinopse */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mb-8 pb-8 border-b border-rose-100/50"
            >
              <p className="text-slate-700 text-base md:text-lg leading-relaxed font-medium">
                {livro.synopsis ||
                  `Acompanhe a discussão sobre "${livro.title}" no Lily Book Club.
                Venha debater, compartilhar suas impressões e descobrir novas perspectivas sobre
                esta obra com nossa comunidade de leitoras.`}
              </p>
            </motion.div>

            {/* Estatísticas da Comunidade */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="grid grid-cols-3 gap-4 mb-10"
            >
              {/* Leitoras */}
              <div className="bg-gradient-to-br from-rose-50/80 to-rose-100/40 rounded-2xl p-4 border border-rose-100/50 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Users size={16} className="text-rose-600" />
                  <p className="text-xs font-black text-rose-600 uppercase tracking-wider">
                    Leitoras
                  </p>
                </div>
                <p className="text-2xl font-black text-slate-900">
                  {stats.leitoras}
                </p>
                <p className="text-xs text-slate-500 mt-1">Lendo agora</p>
              </div>

              {/* Discussões */}
              <div className="bg-gradient-to-br from-blue-50/80 to-blue-100/40 rounded-2xl p-4 border border-blue-100/50 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-2">
                  <MessageCircle size={16} className="text-blue-600" />
                  <p className="text-xs font-black text-blue-600 uppercase tracking-wider">
                    Discussões
                  </p>
                </div>
                <p className="text-2xl font-black text-slate-900">
                  {stats.comentarios}
                </p>
                <p className="text-xs text-slate-500 mt-1">Comentários</p>
              </div>

              {/* Avaliação */}
              <div className="bg-gradient-to-br from-amber-50/80 to-amber-100/40 rounded-2xl p-4 border border-amber-100/50 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Star size={16} className="text-amber-600 fill-amber-600" />
                  <p className="text-xs font-black text-amber-600 uppercase tracking-wider">
                    Avaliação
                  </p>
                </div>
                <p className="text-2xl font-black text-slate-900">
                  {stats.avaliacao}
                </p>
                <p className="text-xs text-slate-500 mt-1">Média do clube</p>
              </div>
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="pt-4"
            >
              {/* CTA Primário */}
              <a
                href="https://chat.whatsapp.com/ECWRWKxIwZOE8ccqDXITSb?s=cl&p=i&ilr=0&amv=0"
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full group relative overflow-hidden rounded-2xl bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 px-8 py-4 text-white font-black text-sm uppercase tracking-widest transition-all duration-300 shadow-lg hover:shadow-2xl hover:-translate-y-1"
              >
                <span className="relative z-10 flex items-center justify-center gap-2 w-full">
                  <BookOpen size={18} />
                  Participar da Discussão
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-rose-400 to-rose-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </a>
            </motion.div>

            {/* Meta ou Call to Action Menor */}
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.55 }}
              className="mt-6 text-xs text-slate-500 font-medium"
            >
              📖 Participe de nosso próximo encontro para discutir este livro
              com a comunidade
            </motion.p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ReadingShowcaseView;
